import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import { CoreDataEntity, IPersistable, ObjectTypeInfoViewModel, PersistMode } from '@barthauer/abc/core/data';
import { ResourceManager } from '@barthauer/abc/core/resources';
import {
  AbcContainerService,
  AbcDropDownBoxComponent,
  AbcFormComponent,
  AbcNotificationComponent,
  AbcTextBoxComponent,
  IForm,
  INotification,
  ItemType,
  ToastType,
  _AbcCheckBoxComponent,
} from '@barthauer/abc/ui-components';
import { ListDataSource } from 'libs/abc/arena/src/lib/core/models/data.model';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, map, takeUntil, tap } from 'rxjs/operators';
import { Property, PropertyDocumentBase, PropertyModel, PropertyType } from '../../models/proprety.model';
import { PropertyViewModel } from '../../viewModels/property.viewModel';
import { PropertiesService } from '../properties.service';

@Component({
  selector: 'properties-editor-form',
  templateUrl: './properties-editor-form.component.html',
  styleUrls: ['./properties-editor-form.component.scss'],
  standalone: false,
})
export class PropertiesEditorFormComponent implements OnInit, OnDestroy {
  @ViewChild('abc', { static: true, read: ViewContainerRef }) abc: ViewContainerRef;
  @ViewChild('abcform', { static: true, read: ViewContainerRef }) abcForm: ViewContainerRef;
  @ViewChild('abcNotification', { static: true, read: ViewContainerRef }) abcNotification: ViewContainerRef;

  public parentId: string;
  private parentObjectType: string;
  private controls = {};
  private requery: boolean;
  private newValue;
  private status = {};
  private persistOutputs = {};
  private isClicked: boolean = false;
  private propertyVm: PropertyViewModel;
  private existingProperties: PropertyModel[] = [];
  private persistenceCategory = 'PropertiesService';

  public showButton: boolean = false;
  public res = ResourceManager.getResources('abc-common-editors-properties');
  public propertiesExist: boolean = false;

  @Input() set parentInfo(data: CoreDataEntity) {
    if (this.parentId && this.parentId !== data.id) {
      this.abcCore.persist.clearPendingObjects(this.persistenceCategory);
    }

    if (data.id) {
      this.parentId = data.id;
      this.parentObjectType = data.objectType.toString();

      this.initPropertiesEditorForm();
    } else {
      this.parentId = null;
      this.parentObjectType = null;
    }
  }
  @Input() isReadOnly: boolean;

  private unsubscribe$ = new Subject<void>();

  constructor(
    private abcContainer: AbcContainerService,
    private propertiesService: PropertiesService,
    private abcCore: AbcCore,
    private cdr: ChangeDetectorRef, // ← added
  ) {
    const table: HTMLTableElement = document.getElementById('properties-form') as HTMLTableElement;
    if (table !== null) {
      table.innerHTML = '';
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  ngOnInit(): void {
    this.abcCore.persist.afterPersistPendingChanges$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.requery) {
        this.requery = false;
        this.existingProperties = [];
        this.initPropertiesEditorForm();
      } else {
        for (const property in this.controls) {
          if (this.existingProperties[property]) {
            this.existingProperties[property].TextValue = this.controls[property]?.value;
          }
          this.persistOutputs[property] = this.controls[property]?.value;
        }
      }
    });
  }

  private async initPropertiesEditorForm() {
    if (this.parentId) {
      this.controls = [];
      if (this.parentObjectType) {
        const otvm: ObjectTypeInfoViewModel = await this.abcCore.objectSpace
          .getObjectType(this.parentObjectType)
          .toPromise();
        this.parentObjectType = otvm.name;
      }
      this.generatePropertyEditorForm()
        .pipe(
          concatMap((pvm) => {
            return this.propertiesService.prepareRequestProperty(this.parentId).pipe(
              tap((properties) => {
                if (properties?.length) {
                  properties?.forEach((element) => {
                    if (this.controls[element.Designation]) {
                      this.controls[element.Designation].value = element.TextValue;
                    } else {
                      this.createAdditionalsInputControls(
                        new Property({ name: element.Designation, type: '0', visibility: 1 }),
                        true,
                      );
                      this.controls[element.Designation].value = element.TextValue;
                    }
                    this.existingProperties[element.Designation] = element;
                  });
                }
              }),
            );
          }),
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe();
    }
  }

  private createAdditionalsInputControls(property, showProperty) {
    const table: HTMLTableElement = document.getElementById('properties-form') as HTMLTableElement;
    this.createAbcUiElements([property], table, showProperty);
  }

  private generatePropertyEditorForm(): Observable<any> {
    const element = document.getElementById('properties-form');
    if (element) {
      element.innerHTML = '';
    }

    return this.propertiesService.getProperties().pipe(
      concatMap((properties) => {
        const propertyDoc = new PropertyDocumentBase(properties);
        this.propertyVm = new PropertyViewModel(propertyDoc);
        return this.propertiesService.queryVisibleColumns(
          this.parentObjectType,
          this.parentId,
          this.propertyVm.propertiesByOT[this.parentObjectType],
        );
      }),
      map((visibleProperties) => {
        if (visibleProperties?.length) {
          this.propertyVm.setPropertyVisibility(this.parentObjectType, visibleProperties);
        } else {
          this.propertiesExist = false;
          this.cdr.detectChanges(); // ← added
        }
        this.propertiesService.propertiesVM = this.propertyVm;
        this.createPropertiesTable(this.propertyVm);
        return visibleProperties ? this.propertyVm : of({});
      }),
    );
  }

  private createPropertiesTable(pm: PropertyViewModel) {
    const properties = pm.getPropertiesByCategory(this.parentObjectType);
    const table: HTMLTableElement = document.getElementById('properties-form') as HTMLTableElement;
    properties?.forEach((properties: Property[], category) => {
      const emptyTr: HTMLTableRowElement = document.createElement('tr');
      emptyTr.setAttribute('style', 'height:20px');
      if (table) {
        table.appendChild(emptyTr);
      }
      if (category && category !== 'noCategory') {
        const tr: HTMLTableRowElement = document.createElement('tr');
        const td = document.createElement('td');
        td.appendChild(this.createGroupCaption(category));
        td.setAttribute('style', 'width:100%;padding-right:15px;padding-bottom:20px');
        td.colSpan = 2;
        tr.appendChild(td);
        if (table) {
          table.appendChild(tr);
        }
      }
      if (properties) {
        this.createAbcUiElements(properties, table);
      }
    });
    return properties;
  }

  private createAbcUiElements(
    properties: Property[],
    table: HTMLTableElement,
    showProperty?: boolean,
  ): HTMLTableElement {
    let tr: HTMLTableRowElement = document.createElement('tr');
    let index = 0;
    properties?.forEach((property, arrIndex) => {
      this.propertiesExist = true;
      if (property.visibility === 1 || showProperty) {
        if (index === 2) {
          tr = document.createElement('tr');
          const emptyTr: HTMLTableRowElement = document.createElement('tr');
          emptyTr.setAttribute('style', 'height:20px');
          if (table) {
            table.appendChild(emptyTr);
          }
          index = 0;
        }
        const td = document.createElement('td');
        td.setAttribute('style', 'padding-right:20px;');
        index++;
        switch (property.type) {
          case PropertyType.Text:
            const input = this.createAbcTextBoxComponent(property.name);
            td.appendChild(input.domElement);
            tr.appendChild(td);
            break;
          case PropertyType.Number:
            td.appendChild(this.createAbcTextBoxComponent(property.name).domElement);
            tr.appendChild(td);
            break;
          case PropertyType.Boolean:
            td.appendChild(this.createAbcCheckboxComponent(property.name).domElement);
            tr.appendChild(td);
            break;
          case PropertyType.Picklist:
            td.appendChild(this.createAbcDropDownBoxComponent(property.name, property.picklist).domElement);
            tr.appendChild(td);
            break;
          default:
            td.appendChild(this.createAbcTextBoxComponent(property.name).domElement);
            tr.appendChild(td);
            break;
        }
        if (index === 2 && arrIndex !== properties.length - 1) {
          if (table) {
            table.appendChild(tr);
          }
        }
      }
      if (arrIndex === properties.length - 1) {
        if (table) {
          table.appendChild(tr);
        }
        const emptyTr: HTMLTableRowElement = document.createElement('tr');
        emptyTr.setAttribute('style', 'height:20px');
        if (table) {
          table.appendChild(emptyTr);
        }
      }
    });
    this.cdr.detectChanges(); // ← added
    return table;
  }

  public saveProperties() {
    for (const property in this.controls) {
      let propertyCount = 0;
      if (this.existingProperties) {
        for (let key in this.existingProperties) {
          if (Object.prototype.hasOwnProperty.call(this.existingProperties, key)) {
            const element = this.existingProperties[key];
            if (property === element.Designation) {
              propertyCount++;
              const objectKey: IPersistable = {
                id: element.Id,
                objectType: 'Property',
                persistenceCategory: this.persistenceCategory,
                changesPersisted: (persistMode) => (persistMode = PersistMode.Update),
                revertChanges: () => {},
              };
              if (this.controls[property].value !== element.TextValue) {
                this.abcCore.persist.clearPendingObjects(this.persistenceCategory);
                if (!CompareHelper.isNullorEmpty(this.controls[property].value)) {
                  this.abcCore.persist.addObjectValue(objectKey, 'Designation', property);
                  this.abcCore.persist.addObjectValue(objectKey, 'TextValue', this.controls[property].value);
                  this.abcCore.persist.addObjectValue(objectKey, 'ParentId', this.parentId);
                  this.abcCore.persist.addObjectValue(objectKey, 'ParentObjectType', this.parentObjectType);
                  this.abcCore.persist.persistPendingChanges(this.persistenceCategory);
                } else {
                  this.requery = true;
                  this.abcCore.persist.addObjectForDelete(objectKey);
                  this.abcCore.persist.persistPendingChanges(this.persistenceCategory);
                  delete this.existingProperties[key];
                }
              }
            }
          }
        }
      }
      if (propertyCount === 0) {
        const objectKey: IPersistable = {
          id: null,
          objectType: 'Property',
          persistenceCategory: this.persistenceCategory,
          changesPersisted: (persistMode: PersistMode) => (persistMode = PersistMode.Insert),
          revertChanges: () => {},
        };
        this.requery = true;
        this.abcCore.persist.clearPendingObjects(this.persistenceCategory);
        this.abcCore.persist.addObjectForInsert(objectKey);
        this.abcCore.persist.addObjectValue(objectKey, 'Designation', property);
        this.abcCore.persist.addObjectValue(objectKey, 'TextValue', this.controls[property].value);
        this.abcCore.persist.addObjectValue(objectKey, 'ParentId', this.parentId);
        this.abcCore.persist.addObjectValue(objectKey, 'ParentObjectType', this.parentObjectType);
        this.abcCore.persist.persistPendingChanges(this.persistenceCategory);
      }
    }
    this.createAbcNotificationComponent();
    this.isClicked = true;
    this.showButton = false;
  }

  private createAbcNotificationComponent() {
    const notifAttr: INotification = {
      id: 'error-notification',
      message: this.res('PropertiesEditorForm-Success'),
      type: ToastType.Success,
      displayTime: 4000,
    };
    const abcNotification = this.abcContainer.createComponent(
      this.abcNotification,
      AbcNotificationComponent,
      notifAttr,
    );
    const abcNotificationRef: AbcNotificationComponent = <AbcNotificationComponent>(
      abcNotification.componentRef.instance
    );
    return {
      domElement: abcNotification.domElement,
      componentInstance: abcNotificationRef,
    };
  }

  private createGroupCaption(caption: string): HTMLElement {
    const formSettings: IForm = {
      id: 'form' + caption,
      caption: caption,
      itemType: ItemType.Group,
      setFormStyle: (style) => {},
    };
    const form = this.abcContainer.createComponent(this.abcForm, AbcFormComponent, formSettings);
    return form.domElement;
  }

  private createAbcCheckboxComponent(text: string) {
    const abcCheckbox = this.abcContainer.createComponent(this.abc, _AbcCheckBoxComponent);
    const abcCheckboxRef: _AbcCheckBoxComponent = <_AbcCheckBoxComponent>abcCheckbox.componentRef.instance;
    this.controls[text] = abcCheckboxRef;
    let initialValue: string;
    abcCheckboxRef.text = text;
    if (this.isReadOnly) {
      abcCheckboxRef.readOnly = true;
    } else {
      abcCheckboxRef.readOnly = false;
    }
    abcCheckboxRef.onValueChanged.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      let countTrue = 0;
      if (this.isClicked === true) {
        initialValue = this.persistOutputs[text];
      }
      this.newValue = event.value;
      if (!initialValue && initialValue !== '') {
        initialValue = this.controls[text]?.value || '';
      }
      if (initialValue !== event.value) {
        this.status[text] = true;
      } else {
        this.status[text] = false;
      }
      for (const key in this.status) {
        if (Object.prototype.hasOwnProperty.call(this.status, key)) {
          const element = this.status[key];
          if (element === true) countTrue++;
        }
      }
      if (countTrue > 0) {
        this.showButton = true;
      } else {
        this.showButton = false;
      }
    });
    return {
      domElement: abcCheckbox.domElement,
      componentInstance: abcCheckboxRef,
    };
  }

  private createAbcTextBoxComponent(text: string) {
    let initialValue: string;
    const abcTextBox = this.abcContainer.createComponent(this.abc, AbcTextBoxComponent);
    const abcTextBoxRef: AbcTextBoxComponent = <AbcTextBoxComponent>abcTextBox.componentRef.instance;
    this.controls[text] = abcTextBoxRef;
    abcTextBoxRef.onValueChanged.pipe(takeUntil(this.unsubscribe$)).subscribe((result) => {
      this.controls[text].value = result.value;
    });
    if (this.isReadOnly) {
      abcTextBoxRef.readOnly = true;
    } else {
      abcTextBoxRef.readOnly = false;
    }
    abcTextBoxRef.label = text;
    abcTextBoxRef.focusStateEnabled = false;
    abcTextBoxRef.onInput.pipe(takeUntil(this.unsubscribe$)).subscribe((event) => {
      let countTrue = 0;
      if (this.isClicked === true) {
        initialValue = this.persistOutputs[text];
      }
      if (!initialValue && initialValue !== '') {
        initialValue = this.controls[text].value || '';
      }
      this.newValue = event.event?.target.value;
      if (initialValue !== this.newValue) {
        this.status[text] = true;
      } else {
        this.status[text] = false;
      }
      for (const key in this.status) {
        if (Object.prototype.hasOwnProperty.call(this.status, key)) {
          const element = this.status[key];
          if (element === true) countTrue++;
        }
      }
      if (countTrue > 0) {
        this.showButton = true;
      } else {
        this.showButton = false;
      }
    });
    return { domElement: abcTextBox.domElement, componentInstance: abcTextBoxRef };
  }

  private createAbcDropDownBoxComponent(text: string, picklistItem: string) {
    let defaultSelectedRow = {};
    let initialValue: string;
    const abcDropDownbox = this.abcContainer.createComponent(this.abc, AbcDropDownBoxComponent);
    const abcDropDownBoxRef: AbcDropDownBoxComponent = <AbcDropDownBoxComponent>abcDropDownbox.componentRef.instance;
    this.controls[text] = abcDropDownBoxRef;
    abcDropDownBoxRef.placeholder = text;
    abcDropDownBoxRef.abcGrid.allowColumnResizing = true;
    if (this.isReadOnly) {
      abcDropDownBoxRef.readOnly = true;
    } else {
      abcDropDownBoxRef.readOnly = false;
    }
    abcDropDownBoxRef.onInitialized.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      const dataSourceMap: Map<string, any> = new Map();
      this.propertiesService
        .prepareRequestPickListItem(picklistItem)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((data: ListDataSource) => {
          abcDropDownBoxRef.displayExpr = abcDropDownBoxRef?.displayExpr || this.checkWhatToShowInPicklist(data?.Data);
          abcDropDownBoxRef.valueExpr = abcDropDownBoxRef?.valueExpr;
          map((data: any) => {
            abcDropDownBoxRef.displayExpr =
              abcDropDownBoxRef?.displayExpr || this.checkWhatToShowInPicklist(data?.Data);
            data?.Data?.forEach((element) => {
              dataSourceMap.set(element[<string>abcDropDownBoxRef.displayExpr], element);
            });
            return data;
          });
          const dropDownData = this.prepareDropDownBoxSource(data);
          abcDropDownBoxRef.valueExpr =
            abcDropDownBoxRef?.valueExpr || this.checkWhatToSaveInPicklist(dropDownData.data);
          abcDropDownBoxRef.gridColumns = dropDownData.columns;
          abcDropDownBoxRef.displayExpr =
            abcDropDownBoxRef?.displayExpr || this.checkWhatToShowInPicklist(dropDownData.data);
          abcDropDownBoxRef.dataSource = dropDownData.data;
        });
    });

    abcDropDownBoxRef.onValueChanged.pipe(takeUntil(this.unsubscribe$)).subscribe((result) => {
      let countTrue = 0;
      if (this.isClicked === true) {
        initialValue = this.persistOutputs[text];
      }
      if (!initialValue && initialValue !== '') {
        initialValue = this.controls[text].value || '';
      }
      this.newValue = abcDropDownBoxRef.text;
      if (initialValue !== this.newValue) {
        this.status[text] = true;
        this.controls[text].value = result.value;
      } else {
        this.status[text] = false;
      }
      for (const key in this.status) {
        if (Object.prototype.hasOwnProperty.call(this.status, key)) {
          const element = this.status[key];
          if (element === true) countTrue++;
        }
      }
      if (countTrue > 0) {
        this.showButton = true;
      } else {
        this.showButton = false;
      }
    });
    abcDropDownBoxRef.onOpened.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      const selectedRow = abcDropDownBoxRef.value;
      if (selectedRow) {
        abcDropDownBoxRef.abcGrid.instance.navigateToRow(selectedRow);
      }
      if (defaultSelectedRow) {
        abcDropDownBoxRef.abcGrid.instance.selectRows(
          defaultSelectedRow[this.checkWhatToSaveInPicklist(abcDropDownBoxRef.dataSource)],
          true,
        );
        abcDropDownBoxRef.abcGrid.instance.navigateToRow(defaultSelectedRow[<string>abcDropDownBoxRef.valueExpr]);
      }
      abcDropDownBoxRef.displayExpr = abcDropDownBoxRef?.displayExpr;
      abcDropDownBoxRef.valueExpr = abcDropDownBoxRef?.valueExpr;
    });
    return {
      domElement: abcDropDownbox.domElement,
      componentInstance: abcDropDownBoxRef,
    };
  }

  public prepareDropDownBoxSource(data: ListDataSource) {
    const lists = new ListDataSource(data);
    const columnHeader = [];
    for (let index = 0; index < lists.ColumnInfos?.length; index++) {
      const element = lists.ColumnInfos[index];
      if (element.Name === 'Id') {
        columnHeader.push({
          dataField: element.Name,
          name: element.Name,
          type: 'string',
          visible: false,
        });
      } else {
        columnHeader.push({ dataField: element.Name, name: element.Caption, caption: element.Caption, type: 'string' });
      }
    }
    const gridData = lists.Data;
    return { columns: columnHeader, data: gridData };
  }

  private checkWhatToSaveInPicklist(data): string {
    if (data) {
      if (data[0]?.Id) return 'Id';
      if (data[0]?.Value) return 'Value';
      if (data[0]?.Identifier) return 'Identifier';
    }
  }

  private checkWhatToShowInPicklist(data): string {
    if (data) {
      if (data[0]?.DisplayText___HIDDEN) return 'DisplayText___HIDDEN';
      if (data[0]?.Name) return 'Name';
      if (data[0]?.Description) return 'Description';
      if (data[0]?.Caption) return 'Caption';
      if (data[0]?.Bezeichnung) return 'Bezeichnung';
      if (data[0]?.Designation) return 'Designation';
      if (data[0]?.Identifier) return 'Identifier';
    }
  }
}
